import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Print as PrintIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  Description as DescriptionIcon,
  Public as PublicIcon,
  Publish as PublishIcon,
  Unpublished as UnpublishedIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Fair } from '../../types/fair.types';
import {
  reportingService,
  StudentAward,
  ReportStatus,
} from '../../services/reporting.service';
import { db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';

// Types and Interfaces
type FairSummary = Pick<Fair, 'id' | 'name'>;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Ceremony Report Component
interface CeremonyReportProps {
  students: StudentAward[];
  fairName: string;
}

function CeremonyReport({ students, fairName }: CeremonyReportProps) {
  const [projectNames, setProjectNames] = React.useState<
    Record<string, string>
  >({});

  // Fetch project names for special awards
  React.useEffect(() => {
    const fetchProjectNames = async () => {
      const specialAwards = students.filter((s) => s.isSpecialAward === true);
      const projectIds = new Set<string>();

      specialAwards.forEach((award) => {
        if (award.projectId) {
          projectIds.add(award.projectId);
        }
      });

      const names: Record<string, string> = {};
      await Promise.all(
        Array.from(projectIds).map(async (projectId) => {
          try {
            const projectDoc = await getDoc(doc(db, 'projects', projectId));
            if (projectDoc.exists()) {
              const projectData = projectDoc.data();
              names[projectId] = projectData.name || 'Unknown Project';
            } else {
              names[projectId] = 'Unknown Project';
            }
          } catch (error) {
            console.error(`Error fetching project ${projectId}:`, error);
            names[projectId] = 'Unknown Project';
          }
        })
      );
      setProjectNames(names);
    };

    if (students.length > 0) {
      fetchProjectNames();
    }
  }, [students]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    const content = generateReportContent();
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ceremony-script.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const generateReportContent = () => {
    let content = 'SCIENCE FAIR CEREMONY SCRIPT\n';
    content += '================================\n\n';
    content += `Fair: ${fairName}\n\n`;

    // Separate standard awards and special awards
    const standardAwards = students.filter((s) => !s.isSpecialAward);
    const specialAwards = students.filter((s) => s.isSpecialAward === true);

    // Group standard awards by category
    const groupedByAward = standardAwards.reduce(
      (acc, student) => {
        if (!acc[student.award]) {
          acc[student.award] = [];
        }
        acc[student.award].push(student);
        return acc;
      },
      {} as Record<string, StudentAward[]>
    );

    // Add standard awards
    Object.entries(groupedByAward).forEach(([award, awardStudents]) => {
      content += `${award.toUpperCase()}\n`;
      content += '-'.repeat(award.length) + '\n\n';

      awardStudents.forEach((student) => {
        content += `${student.placement}: ${student.name}\n`;
        content += `   Pronunciation: ${student.phoneticName || 'Not provided'}\n`;
        content += `   School: ${student.school}\n\n`;
      });
      content += '\n';
    });

    // Add special awards section
    if (specialAwards.length > 0) {
      content += 'SPECIAL AWARDS\n';
      content += '==============\n\n';

      // Group by award, then by project
      const groupedBySpecialAward = specialAwards.reduce(
        (acc, student) => {
          if (!acc[student.award]) {
            acc[student.award] = {};
          }
          const projectId = student.projectId || 'individual';
          if (!acc[student.award][projectId]) {
            acc[student.award][projectId] = [];
          }
          acc[student.award][projectId].push(student);
          return acc;
        },
        {} as Record<string, Record<string, StudentAward[]>>
      );

      Object.entries(groupedBySpecialAward).forEach(([award, projects]) => {
        content += `${award}\n`;
        content += '-'.repeat(award.length) + '\n\n';

        Object.entries(projects).forEach(([projectId, projectStudents]) => {
          if (projectId !== 'individual' && projectStudents.length > 1) {
            // Group as project - show project name with all students
            const studentNames = projectStudents.map((s) => s.name).join(', ');
            const phoneticNames = projectStudents
              .map((s) => s.phoneticName || 'Not provided')
              .join(', ');
            const schools = Array.from(
              new Set(projectStudents.map((s) => s.school))
            ).join(', ');
            content += `Project: ${studentNames}\n`;
            content += `   Pronunciation: ${phoneticNames}\n`;
            content += `   School: ${schools}\n\n`;
          } else {
            // Individual students
            projectStudents.forEach((student) => {
              content += `${student.name}\n`;
              content += `   Pronunciation: ${student.phoneticName || 'Not provided'}\n`;
              content += `   School: ${student.school}\n\n`;
            });
          }
        });
        content += '\n';
      });
    }

    return content;
  };

  // Separate standard and special awards
  const standardAwards = students.filter((s) => !s.isSpecialAward);
  const specialAwards = students.filter((s) => s.isSpecialAward === true);

  const groupedStudents = standardAwards.reduce(
    (acc, student) => {
      if (!acc[student.award]) {
        acc[student.award] = [];
      }
      acc[student.award].push(student);
      return acc;
    },
    {} as Record<string, StudentAward[]>
  );

  // Group special awards by award name, then by project
  const groupedSpecialAwards = specialAwards.reduce(
    (acc, student) => {
      if (!acc[student.award]) {
        acc[student.award] = {};
      }
      const projectId = student.projectId || 'individual';
      if (!acc[student.award][projectId]) {
        acc[student.award][projectId] = [];
      }
      acc[student.award][projectId].push(student);
      return acc;
    },
    {} as Record<string, Record<string, StudentAward[]>>
  );

  if (students.length === 0) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          Ceremony Script (Report 1)
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          For MC use during award ceremony
        </Typography>
        <Box textAlign="center" py={4} color="text.secondary">
          No awards data available yet. Generate report data to create the
          ceremony script.
        </Box>
      </Paper>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Box>
            <Typography variant="h6" component="h2">
              Ceremony Script (Report 1)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Internal report for MC with phonetic pronunciations and detailed
              information
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              onClick={handlePrint}
              startIcon={<PrintIcon />}
              size="small"
            >
              Print
            </Button>
            <Button
              variant="outlined"
              onClick={handleDownload}
              startIcon={<DownloadIcon />}
              size="small"
            >
              Download
            </Button>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ '& .print-hidden': { '@media print': { display: 'none' } } }}>
        <Box textAlign="center" borderBottom={1} pb={2} mb={4}>
          <Typography variant="h4" component="h1" gutterBottom>
            {fairName} Award Ceremony
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Master of Ceremonies Script
          </Typography>
        </Box>

        {Object.entries(groupedStudents).map(([award, awardStudents]) => (
          <Paper key={award} sx={{ p: 2, mb: 2 }}>
            <Typography variant="h5" component="h3" gutterBottom>
              {award}
            </Typography>
            <Box>
              {awardStudents.map((student) => (
                <Box
                  key={student.studentId}
                  sx={{
                    borderLeft: 4,
                    borderColor: 'primary.main',
                    pl: 2,
                    py: 1,
                    mb: 2,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Chip
                      label={student.placement}
                      variant="outlined"
                      size="small"
                    />
                    <Typography variant="subtitle1" fontWeight="medium">
                      {student.name}
                    </Typography>
                  </Box>

                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <strong>Pronunciation:</strong>{' '}
                    {student.phoneticName || 'Not provided'}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    <strong>School:</strong> {student.school}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        ))}

        {Object.keys(groupedSpecialAwards).length > 0 && (
          <Paper sx={{ p: 2, mb: 2, border: 2, borderColor: 'warning.main' }}>
            <Typography
              variant="h5"
              component="h3"
              gutterBottom
              color="warning.main"
            >
              Special Awards
            </Typography>
            {Object.entries(groupedSpecialAwards).map(([award, projects]) => (
              <Box key={award} sx={{ mb: 3 }}>
                <Typography variant="h6" component="h4" gutterBottom>
                  {award}
                </Typography>
                {Object.entries(projects).map(
                  ([projectId, projectStudents]) => {
                    const projectName =
                      projectId === 'individual'
                        ? 'Individual Students'
                        : projectNames[projectId] || 'Unknown Project';

                    return (
                      <Box
                        key={projectId}
                        sx={{
                          borderLeft: 4,
                          borderColor: 'warning.main',
                          pl: 2,
                          py: 1,
                          mb: 2,
                          backgroundColor: 'rgba(255, 152, 0, 0.05)',
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          fontWeight="bold"
                          mb={1}
                        >
                          {projectName}
                        </Typography>
                        {projectStudents.map((student) => (
                          <Box key={student.studentId} sx={{ ml: 2, mb: 1 }}>
                            <Typography variant="body1" fontWeight="medium">
                              {student.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              mb={0.5}
                            >
                              <strong>Pronunciation:</strong>{' '}
                              {student.phoneticName || 'Not provided'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <strong>School:</strong> {student.school}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    );
                  }
                )}
              </Box>
            ))}
          </Paper>
        )}
      </Box>
    </Box>
  );
}

// Public Report Component
interface PublicReportProps {
  students: StudentAward[];
  fairName: string;
}

function PublicReport({ students, fairName }: PublicReportProps) {
  const [projectNames, setProjectNames] = React.useState<
    Record<string, string>
  >({});

  // Fetch project names for special awards
  React.useEffect(() => {
    const fetchProjectNames = async () => {
      const specialAwards = students.filter((s) => s.isSpecialAward === true);
      const projectIds = new Set<string>();

      specialAwards.forEach((award) => {
        if (award.projectId) {
          projectIds.add(award.projectId);
        }
      });

      const names: Record<string, string> = {};
      await Promise.all(
        Array.from(projectIds).map(async (projectId) => {
          try {
            const projectDoc = await getDoc(doc(db, 'projects', projectId));
            if (projectDoc.exists()) {
              const projectData = projectDoc.data();
              names[projectId] = projectData.name || 'Unknown Project';
            } else {
              names[projectId] = 'Unknown Project';
            }
          } catch (error) {
            console.error(`Error fetching project ${projectId}:`, error);
            names[projectId] = 'Unknown Project';
          }
        })
      );
      setProjectNames(names);
    };

    if (students.length > 0) {
      fetchProjectNames();
    }
  }, [students]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    const content = generatePublicReportContent();
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'science-fair-results.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleShare = () => {
    const content = generatePublicReportContent();
    if (navigator.share) {
      navigator.share({
        title: 'Science Fair Results',
        text: content,
      });
    } else {
      navigator.clipboard.writeText(content);
      alert('Results copied to clipboard!');
    }
  };

  const generatePublicReportContent = () => {
    let content = 'SCIENCE FAIR RESULTS\n';
    content += '====================\n\n';
    content += `Fair: ${fairName}\n\n`;

    // Separate standard awards and special awards
    const standardAwards = students.filter((s) => !s.isSpecialAward);
    const specialAwards = students.filter((s) => s.isSpecialAward === true);

    // Group standard awards by category
    const groupedByAward = standardAwards.reduce(
      (acc, student) => {
        if (!acc[student.award]) {
          acc[student.award] = [];
        }
        acc[student.award].push(student);
        return acc;
      },
      {} as Record<string, StudentAward[]>
    );

    // Add standard awards
    Object.entries(groupedByAward).forEach(([award, awardStudents]) => {
      content += `${award}\n`;
      content += '-'.repeat(award.length) + '\n';

      awardStudents.forEach((student) => {
        content += `${student.placement}: ${student.name} - ${student.school}\n`;
      });
      content += '\n';
    });

    // Add special awards section
    if (specialAwards.length > 0) {
      content += 'SPECIAL AWARDS\n';
      content += '==============\n\n';

      // Group by award, then by project
      const groupedBySpecialAward = specialAwards.reduce(
        (acc, student) => {
          if (!acc[student.award]) {
            acc[student.award] = {};
          }
          const projectId = student.projectId || 'individual';
          if (!acc[student.award][projectId]) {
            acc[student.award][projectId] = [];
          }
          acc[student.award][projectId].push(student);
          return acc;
        },
        {} as Record<string, Record<string, StudentAward[]>>
      );

      Object.entries(groupedBySpecialAward).forEach(([award, projects]) => {
        content += `${award}\n`;
        content += '-'.repeat(award.length) + '\n';

        Object.entries(projects).forEach(([projectId, projectStudents]) => {
          // For text output, we'll need to fetch project names or use a placeholder
          // Since we can't easily fetch in the text generation, we'll group students
          if (projectId !== 'individual' && projectStudents.length > 1) {
            // Group as project
            const studentNames = projectStudents.map((s) => s.name).join(', ');
            const schools = Array.from(
              new Set(projectStudents.map((s) => s.school))
            ).join(', ');
            content += `Project: ${studentNames} - ${schools}\n`;
          } else {
            // Individual students
            projectStudents.forEach((student) => {
              content += `${student.name} - ${student.school}\n`;
            });
          }
        });
        content += '\n';
      });
    }

    return content;
  };

  // Separate standard and special awards for display
  const standardAwardsForTable = students.filter((s) => !s.isSpecialAward);
  const specialAwardsForTable = students.filter(
    (s) => s.isSpecialAward === true
  );

  const sortedStudents = [...standardAwardsForTable].sort((a, b) => {
    // Sort by award first, then by placement
    if (a.award !== b.award) {
      return a.award.localeCompare(b.award);
    }

    const placementOrder = [
      '1st Place',
      '2nd Place',
      '3rd Place',
      'Honorable Mention',
      'Special Recognition',
      'Participation',
    ];
    const aIndex = placementOrder.indexOf(a.placement);
    const bIndex = placementOrder.indexOf(b.placement);

    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }

    return a.placement.localeCompare(b.placement);
  });

  // Group special awards by award, then by project
  const groupedSpecialAwardsForTable = specialAwardsForTable.reduce(
    (acc, student) => {
      if (!acc[student.award]) {
        acc[student.award] = {};
      }
      const projectId = student.projectId || 'individual';
      if (!acc[student.award][projectId]) {
        acc[student.award][projectId] = [];
      }
      acc[student.award][projectId].push(student);
      return acc;
    },
    {} as Record<string, Record<string, StudentAward[]>>
  );

  if (students.length === 0) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          Public Results (Report 2)
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          For website posting and school distribution
        </Typography>
        <Box textAlign="center" py={4} color="text.secondary">
          No awards data available yet. Generate report data to create the
          public results.
        </Box>
      </Paper>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Box>
            <Typography variant="h6" component="h2">
              Public Results (Report 2)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Clean report for website posting and school distribution (no
              phonetic info)
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              onClick={handlePrint}
              startIcon={<PrintIcon />}
              size="small"
            >
              Print
            </Button>
            <Button
              variant="outlined"
              onClick={handleDownload}
              startIcon={<DownloadIcon />}
              size="small"
            >
              Download
            </Button>
            <Button
              variant="outlined"
              onClick={handleShare}
              startIcon={<ShareIcon />}
              size="small"
            >
              Share
            </Button>
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Box textAlign="center" mb={3}>
          <Typography variant="h4" component="h2">
            Science Fair Results
          </Typography>
          <Typography variant="h6" sx={{ mt: 1 }} color="text.secondary">
            {fairName}
          </Typography>
        </Box>

        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.200' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Student Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>School</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Award</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedStudents.map((student) => (
              <TableRow key={student.studentId}>
                <TableCell sx={{ fontWeight: 'medium' }}>
                  {student.name}
                </TableCell>
                <TableCell>{student.school}</TableCell>
                <TableCell>{student.award}</TableCell>
                <TableCell>
                  <Chip
                    label={student.placement}
                    color={
                      student.placement.includes('1st')
                        ? 'primary'
                        : student.placement.includes('2nd')
                          ? 'secondary'
                          : student.placement.includes('3rd')
                            ? 'success'
                            : 'default'
                    }
                    variant={
                      student.placement.includes('1st') ? 'filled' : 'outlined'
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {Object.keys(groupedSpecialAwardsForTable).length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" gutterBottom color="warning.main">
              Special Awards
            </Typography>
            {Object.entries(groupedSpecialAwardsForTable).map(
              ([award, projects]) => (
                <Box key={award} sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    {award}
                  </Typography>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'warning.light' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          Project/Student
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          School
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(projects).map(
                        ([projectId, projectStudents]) => {
                          const projectName =
                            projectId === 'individual'
                              ? null
                              : projectNames[projectId] || 'Unknown Project';

                          if (projectName && projectStudents.length > 1) {
                            // Show as grouped project
                            const studentNames = projectStudents
                              .map((s) => s.name)
                              .join(', ');
                            const schools = Array.from(
                              new Set(projectStudents.map((s) => s.school))
                            ).join(', ');
                            return (
                              <TableRow key={projectId}>
                                <TableCell sx={{ fontWeight: 'medium' }}>
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      fontWeight="bold"
                                    >
                                      {projectName}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {studentNames}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>{schools}</TableCell>
                              </TableRow>
                            );
                          } else {
                            // Show individual students
                            return projectStudents.map((student) => (
                              <TableRow
                                key={`${student.studentId}-${student.award}`}
                              >
                                <TableCell sx={{ fontWeight: 'medium' }}>
                                  {student.name}
                                </TableCell>
                                <TableCell>{student.school}</TableCell>
                              </TableRow>
                            ));
                          }
                        }
                      )}
                    </TableBody>
                  </Table>
                </Box>
              )
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
}

// Main FairReportsDialog Component
interface FairReportsDialogProps {
  open: boolean;
  onClose: () => void;
  fair: FairSummary | null;
}

const FairReportsDialog: React.FC<FairReportsDialogProps> = ({
  open,
  onClose,
  fair,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [students, setStudents] = useState<StudentAward[]>([]);
  const [reportStatus, setReportStatus] = useState<ReportStatus | null>(null);
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadReportData = useCallback(async () => {
    if (!fair) return;

    try {
      setLoading(true);
      const data = await reportingService.getReportData(fair.id);
      if (data) {
        setStudents(data.studentAwards);
      } else {
        setStudents([]);
      }
    } catch (error) {
      console.error('Error loading report data:', error);
    } finally {
      setLoading(false);
    }
  }, [fair]);

  // Load report data and subscribe to status when dialog opens
  useEffect(() => {
    if (open && fair) {
      loadReportData();

      const unsubscribe = reportingService.subscribeToReportStatus(
        fair.id,
        (status) => {
          setReportStatus(status);
        }
      );

      return () => unsubscribe();
    }
  }, [open, fair, loadReportData]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleGenerateReport = async () => {
    if (!fair) return;

    try {
      setGenerating(true);
      await reportingService.generateReportData(fair.id, 'admin');
      await loadReportData();
      alert('Report data generated successfully based on judge scores!');
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report data');
    } finally {
      setGenerating(false);
    }
  };

  const handlePublishReport = async () => {
    if (!fair) return;

    try {
      await reportingService.publishReport(fair.id, 'admin');
    } catch (error) {
      console.error('Error publishing report:', error);
    }
  };

  const handleUnpublishReport = async () => {
    if (!fair) return;

    try {
      await reportingService.unpublishReport(fair.id);
    } catch (error) {
      console.error('Error unpublishing report:', error);
    }
  };

  if (!fair) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5" component="h2">
              Reports - {fair.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Generate ceremony scripts and public results from judge scores
            </Typography>
          </Box>
          <Box display="flex" gap={1} alignItems="center">
            <Button
              variant="outlined"
              size="small"
              onClick={handleGenerateReport}
              disabled={generating}
              startIcon={
                generating ? <CircularProgress size={16} /> : <RefreshIcon />
              }
            >
              {generating ? 'Generating...' : 'Generate'}
            </Button>
            {reportStatus?.isPublished ? (
              <>
                <Chip
                  label="Published"
                  color="success"
                  icon={<PublishIcon />}
                  size="small"
                />
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={handleUnpublishReport}
                  startIcon={<UnpublishedIcon />}
                  size="small"
                >
                  Unpublish
                </Button>
              </>
            ) : (
              <>
                <Chip
                  label="Draft"
                  color="default"
                  icon={<UnpublishedIcon />}
                  size="small"
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handlePublishReport}
                  startIcon={<PublishIcon />}
                  size="small"
                  disabled={students.length === 0}
                >
                  Publish
                </Button>
              </>
            )}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="300px"
          >
            <CircularProgress />
          </Box>
        ) : (
          <Paper sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="reporting tabs"
              >
                <Tab
                  icon={<DescriptionIcon />}
                  label="Ceremony Script"
                  iconPosition="start"
                />
                <Tab
                  icon={<PublicIcon />}
                  label="Public Results"
                  iconPosition="start"
                />
              </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
              <CeremonyReport students={students} fairName={fair.name} />
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <PublicReport students={students} fairName={fair.name} />
            </TabPanel>
          </Paper>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FairReportsDialog;
